import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import { Empty, Tooltip, Popover, Input, Checkbox, Button } from 'antd';
import {
  DoubleLeftOutlined,
  DoubleRightOutlined,
  LeftOutlined,
  RightOutlined,
  FilterOutlined
} from '@ant-design/icons';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getPaginationRowModel,
  flexRender
} from '@tanstack/react-table';
import Spinner from '../../CustomComponents/Spinner';
import { createUserColumns } from './userColumnConfig';
import { getAriaSortValue } from '../JobList/columnHelpers';
import * as Styled from '../JobList/TanStackTableStyles';

const ITEMS_PER_PAGE = 50;
const NON_DRAGGABLE_COLUMNS = ['actions'];

const FilterPopoverContent = ({ column }) => {
  const [search, setSearch] = useState('');
  const currentFilter = column.getFilterValue() ?? [];
  const facetedMap = column.getFacetedUniqueValues();

  const options = Array.from(facetedMap.entries())
    .map(([value]) => ({ label: value != null ? String(value) : 'N/A' }))
    .filter(({ label }) => label.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => a.label.localeCompare(b.label));

  return (
    <div style={{ minWidth: 180, maxWidth: 260, padding: '0 8px' }}>
      <Input
        size='small'
        placeholder='Search...'
        value={search}
        onChange={e => setSearch(e.target.value)}
        onClick={e => e.stopPropagation()}
        style={{ marginBottom: 8 }}
        allowClear
      />
      <div style={{ maxHeight: 200, overflowY: 'auto' }}>
        {options.length === 0 ? (
          <div style={{ color: 'rgba(0,0,0,0.45)', fontSize: 12, padding: '4px 0' }}>
            No options
          </div>
        ) : (
          options.map(({ label }) => (
            <div key={label} style={{ padding: '3px 0' }}>
              <Checkbox
                checked={currentFilter.includes(label)}
                onChange={e => {
                  const newValues = e.target.checked
                    ? [...currentFilter, label]
                    : currentFilter.filter(v => v !== label);
                  column.setFilterValue(newValues.length > 0 ? newValues : undefined);
                }}
              >
                <span style={{ fontSize: 13 }}>{label}</span>
              </Checkbox>
            </div>
          ))
        )}
      </div>
      {currentFilter.length > 0 && (
        <div style={{ borderTop: '1px solid #f0f2f5', marginTop: 8, paddingTop: 6, textAlign: 'right' }}>
          <Button type='link' size='small' onClick={() => column.setFilterValue(undefined)}>
            Clear
          </Button>
        </div>
      )}
    </div>
  );
};

const TanStackUserTable = ({ users, tableLoading, searchValue, onEditPassword, onViewLogs, onImpersonate }) => {
  const [theme] = useSelector(state => [state.themeReducer]);

  const [sortingState, setSortingState] = useState([
    { id: 'isOnline', desc: true },
    { id: 'fullName', desc: false }
  ]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState([]);
  const [filterVisible, setFilterVisible] = useState({});
  const [columnVisibility, setColumnVisibility] = useState({});
  const [columnSizing, setColumnSizing] = useState({});
  const [columnOrder, setColumnOrder] = useState([]);
  const [draggedColumnId, setDraggedColumnId] = useState(null);
  const [dragOverColumnId, setDragOverColumnId] = useState(null);

  useEffect(() => {
    setGlobalFilter(searchValue || '');
  }, [searchValue]);

  const handleColumnDragStart = useCallback((e, headerId) => {
    setDraggedColumnId(headerId);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleColumnDragOver = useCallback(
    (e, headerId) => {
      e.preventDefault();
      if (headerId !== draggedColumnId && !NON_DRAGGABLE_COLUMNS.includes(headerId)) {
        e.dataTransfer.dropEffect = 'move';
        setDragOverColumnId(headerId);
      }
    },
    [draggedColumnId]
  );

  const handleColumnDragLeave = useCallback(() => {
    setDragOverColumnId(null);
  }, []);

  const handleColumnDrop = useCallback(
    (e, targetId) => {
      e.preventDefault();
      if (
        !draggedColumnId ||
        draggedColumnId === targetId ||
        NON_DRAGGABLE_COLUMNS.includes(targetId)
      ) {
        setDraggedColumnId(null);
        setDragOverColumnId(null);
        return;
      }
      setColumnOrder(prev => {
        const newOrder = [...prev];
        const fromIdx = newOrder.indexOf(draggedColumnId);
        const toIdx = newOrder.indexOf(targetId);
        newOrder.splice(fromIdx, 1);
        newOrder.splice(toIdx, 0, draggedColumnId);
        return newOrder;
      });
      setDraggedColumnId(null);
      setDragOverColumnId(null);
    },
    [draggedColumnId]
  );

  const handleColumnDragEnd = useCallback(() => {
    setDraggedColumnId(null);
    setDragOverColumnId(null);
  }, []);

  const primaryColor = theme['@primary-color'] || '#47A4EA';

  const columns = useMemo(
    () => createUserColumns({ onEditPassword, onViewLogs, onImpersonate, primaryColor }),
    [onEditPassword, onViewLogs, onImpersonate, primaryColor]
  );

  useEffect(() => {
    setColumnOrder(columns.map(col => col.id));
  }, [columns]);

  const table = useReactTable({
    data: users || [],
    columns,
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: ITEMS_PER_PAGE, pageIndex: 0 } },
    getSortedRowModel: getSortedRowModel(),
    manualSorting: false,
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: 'includesString',
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    state: {
      sorting: sortingState,
      globalFilter,
      columnFilters,
      columnVisibility,
      columnSizing,
      columnOrder
    },
    onSortingChange: setSortingState,
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnSizingChange: setColumnSizing,
    onColumnOrderChange: setColumnOrder,
    getCoreRowModel: getCoreRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getRowId: row => row.id
  });

  const isEmpty = !users || users.length === 0;

  const tableContainerRef = useRef(null);
  const topScrollRef = useRef(null);
  const tableRef = useRef(null);

  useEffect(() => {
    const container = tableContainerRef.current;
    const tableEl = tableRef.current;
    if (!container) return;
    let rafId;
    const updateWidth = () => {
      rafId = requestAnimationFrame(() => {
        if (topScrollRef.current?.firstChild) {
          topScrollRef.current.firstChild.style.width = `${container.scrollWidth}px`;
        }
      });
    };
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(container);
    if (tableEl) observer.observe(tableEl);
    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [users, columnSizing, columnVisibility, columnOrder]);

  const handleTopScroll = () => {
    if (tableContainerRef.current && topScrollRef.current) {
      tableContainerRef.current.scrollLeft = topScrollRef.current.scrollLeft;
    }
  };

  const handleBottomScroll = () => {
    if (topScrollRef.current && tableContainerRef.current) {
      topScrollRef.current.scrollLeft = tableContainerRef.current.scrollLeft;
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Styled.TableContainer
        ref={tableContainerRef}
        onScroll={handleBottomScroll}
        style={{ position: 'relative' }}
      >
        <Styled.TopScrollbar ref={topScrollRef} onScroll={handleTopScroll}>
          <div style={{ height: 1 }} />
        </Styled.TopScrollbar>

        {tableLoading && (
          <Styled.LoadingOverlay>
            <Spinner />
          </Styled.LoadingOverlay>
        )}

        {isEmpty && !tableLoading ? (
          <Styled.EmptyState>
            <Empty description='No users found' />
          </Styled.EmptyState>
        ) : (
          <>
            <Styled.StyledTable
              ref={tableRef}
              role='table'
              aria-label='User management table'
              style={{
                width: table.getTotalSize(),
                minWidth: '100%',
                tableLayout: 'fixed',
                transition: table.getState().columnSizingInfo.isResizingColumn
                  ? 'none'
                  : 'width 0.15s ease'
              }}
            >
              <Styled.StyledTableHead role='rowgroup'>
                {table.getHeaderGroups().map(headerGroup => (
                  <Styled.StyledTableHeaderRow key={headerGroup.id} role='row'>
                    {headerGroup.headers.map(header => {
                      const isDraggable = !NON_DRAGGABLE_COLUMNS.includes(header.id);
                      return (
                        <Styled.DraggableHeader
                          key={header.id}
                          role='columnheader'
                          aria-sort={getAriaSortValue(header.column.getIsSorted())}
                          style={{ width: header.getSize() }}
                          sortable={header.column.getCanSort()}
                          sorted={header.column.getIsSorted()}
                          isDragging={draggedColumnId === header.id}
                          isOver={dragOverColumnId === header.id}
                          onDragOver={isDraggable ? e => handleColumnDragOver(e, header.id) : undefined}
                          onDragLeave={isDraggable ? handleColumnDragLeave : undefined}
                          onDrop={isDraggable ? e => handleColumnDrop(e, header.id) : undefined}
                        >
                          <div
                            draggable={isDraggable}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4,
                              cursor: isDraggable ? 'grab' : 'default'
                            }}
                            onClick={
                              header.column.getCanSort() && !draggedColumnId
                                ? header.column.getToggleSortingHandler()
                                : undefined
                            }
                            tabIndex={header.column.getCanSort() ? 0 : -1}
                            onKeyDown={e => {
                              if (
                                header.column.getCanSort() &&
                                (e.key === 'Enter' || e.key === ' ')
                              ) {
                                header.column.getToggleSortingHandler()(e);
                              }
                            }}
                            onDragStart={isDraggable ? e => handleColumnDragStart(e, header.id) : undefined}
                            onDragEnd={handleColumnDragEnd}
                          >
                            {header.column.getCanSort() && (
                              <Styled.SortIndicator direction={header.column.getIsSorted()}>
                                <span className='sort-arrow-up'>▲</span>
                                <span className='sort-arrow-down'>▼</span>
                              </Styled.SortIndicator>
                            )}

                            <span style={{ flex: 1 }}>
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                            </span>

                            {header.column.getCanFilter() && (
                              <span onClick={e => e.stopPropagation()}>
                                <Popover
                                  content={<FilterPopoverContent column={header.column} />}
                                  trigger='click'
                                  visible={filterVisible[header.id] || false}
                                  onVisibleChange={visible =>
                                    setFilterVisible(prev => ({ ...prev, [header.id]: visible }))
                                  }
                                  placement='bottomRight'
                                >
                                  <FilterOutlined
                                    style={{
                                      fontSize: 12,
                                      cursor: 'pointer',
                                      flexShrink: 0,
                                      color: header.column.getFilterValue()
                                        ? '#47a4ea'
                                        : 'rgba(0,0,0,0.25)'
                                    }}
                                  />
                                </Popover>
                              </span>
                            )}
                          </div>

                          {header.column.getCanResize() && (
                            <Styled.ResizeHandle
                              onMouseDown={e => {
                                e.stopPropagation();
                                header.getResizeHandler()(e);
                              }}
                              onTouchStart={header.getResizeHandler()}
                              isResizing={header.column.getIsResizing()}
                              onClick={e => e.stopPropagation()}
                            />
                          )}
                        </Styled.DraggableHeader>
                      );
                    })}
                  </Styled.StyledTableHeaderRow>
                ))}
              </Styled.StyledTableHead>

              <Styled.StyledTableBody role='rowgroup'>
                {table.getRowModel().rows.map(row => (
                  <Styled.StyledTableRow key={row.id} role='row'>
                    {row.getVisibleCells().map(cell => (
                      <Styled.StyledTableCell key={cell.id} role='cell'>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </Styled.StyledTableCell>
                    ))}
                  </Styled.StyledTableRow>
                ))}
              </Styled.StyledTableBody>
            </Styled.StyledTable>

            <Styled.PaginationContainer>
              <Styled.PaginationInfo>
                Showing{' '}
                {table.getState().pagination.pageIndex * ITEMS_PER_PAGE + 1} to{' '}
                {Math.min(
                  (table.getState().pagination.pageIndex + 1) * ITEMS_PER_PAGE,
                  table.getFilteredRowModel().rows.length
                )}{' '}
                of {table.getFilteredRowModel().rows.length} results
                {globalFilter && ` (filtered from ${users?.length || 0} total)`}
              </Styled.PaginationInfo>

              <Styled.PaginationControls>
                <Tooltip title='First Page'>
                  <Styled.PaginationButton
                    type='text'
                    icon={<DoubleLeftOutlined />}
                    disabled={!table.getCanPreviousPage()}
                    onClick={() => table.setPageIndex(0)}
                    aria-label='First Page'
                  />
                </Tooltip>
                <Tooltip title='Previous Page'>
                  <Styled.PaginationButton
                    type='text'
                    icon={<LeftOutlined />}
                    disabled={!table.getCanPreviousPage()}
                    onClick={() => table.previousPage()}
                    aria-label='Previous Page'
                  />
                </Tooltip>
                <span>
                  Page {table.getState().pagination.pageIndex + 1} of{' '}
                  {table.getPageCount() || 1}
                </span>
                <Tooltip title='Next Page'>
                  <Styled.PaginationButton
                    type='text'
                    icon={<RightOutlined />}
                    disabled={!table.getCanNextPage()}
                    onClick={() => table.nextPage()}
                    aria-label='Next Page'
                  />
                </Tooltip>
                <Tooltip title='Last Page'>
                  <Styled.PaginationButton
                    type='text'
                    icon={<DoubleRightOutlined />}
                    disabled={!table.getCanNextPage()}
                    onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                    aria-label='Last Page'
                  />
                </Tooltip>
              </Styled.PaginationControls>
            </Styled.PaginationContainer>
          </>
        )}
      </Styled.TableContainer>
    </ThemeProvider>
  );
};

export default TanStackUserTable;
